import { useEffect, useState } from 'react'
import type { Box } from './read'

/**
 * A picture's box as the model gave it, [ymin, xmin, ymax, xmax] from 0 to 1000, often runs a little past the picture into the app's
 * background (a dark band under a white chart card). Each edge is trimmed while its row or column is flat and the same colour as the
 * screenshot just outside the box: that is background reaching in, not the picture. An edge with nothing flat outside it loses a
 * flat near-black band of its own (a video frame's letterbox). Measured on a small copy, once per picture.
 */
export type { Box }

const SAMPLE_WIDTH = 360
/** How flat a background row is, and how close to the outside colour, on a 0–255 scale. */
const FLAT = 8, SAME = 14
/** Below this luminance a flat band is a video's black letterbox, never the picture. */
const DARK = 24
/** Never trims more than this share of the box from one edge; a letterbox (a square card in a tall video frame) up to the larger one. */
const MOST = 0.3, MOST_LETTERBOX = 0.45
/** A letterbox may start this share of the box inside the edge, and is one only when at least the last share deep. */
const PROBE = 0.05, LEAST_LETTERBOX = 0.03

const trimmed = new Map<string, Promise<Box>>()

function load(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.decoding = 'async'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('The picture did not load.'))
    image.src = source
  })
}

async function measure(source: string, box: Box): Promise<Box> {
  const image = await load(source)
  const width = Math.min(SAMPLE_WIDTH, image.naturalWidth), height = Math.round(image.naturalHeight * width / image.naturalWidth)
  if (!width || !height) return box
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return box
  context.drawImage(image, 0, 0, width, height)
  // A picture served from another origin cannot be read; it keeps the model's box.
  return trimPixels(context.getImageData(0, 0, width, height).data, width, height, box)
}

/** The box trimmed on an image's RGBA pixels (exported for tests). */
export function trimPixels(pixels: ArrayLike<number>, width: number, height: number, box: Box): Box {
  const line = (fixed: number, from: number, to: number, horizontal: boolean) => {
    let r = 0, g = 0, b = 0, n = 0
    const lum: number[] = []
    for (let i = from; i < to; i++) {
      const at = ((horizontal ? fixed * width + i : i * width + fixed) * 4)
      r += pixels[at]!; g += pixels[at + 1]!; b += pixels[at + 2]!; n++
      lum.push(0.299 * pixels[at]! + 0.587 * pixels[at + 1]! + 0.114 * pixels[at + 2]!)
    }
    const mean = lum.reduce((sum, value) => sum + value, 0) / Math.max(1, n)
    const spread = Math.sqrt(lum.reduce((sum, value) => sum + (value - mean) ** 2, 0) / Math.max(1, n))
    return { colour: [r / n, g / n, b / n], spread }
  }
  const close = (a: number[], b: number[]) => Math.max(...a.map((value, i) => Math.abs(value - b[i]!))) < SAME
  const dark = (colour: number[]) => 0.299 * colour[0]! + 0.587 * colour[1]! + 0.114 * colour[2]! < DARK
  let top = Math.round(box[0] / 1000 * height), left = Math.round(box[1] / 1000 * width)
  let bottom = Math.round(box[2] / 1000 * height), right = Math.round(box[3] / 1000 * width)
  type Line = ReturnType<typeof line>
  const like = (row: Line, colour: number[]) => row.spread < FLAT && close(row.colour, colour)
  // How far an edge moves in, `at(n)` being the line n steps inside it. Against the flat line just outside it (background reaching in),
  // up to MOST, or MOST_LETTERBOX when that line is black; with nothing flat outside (the screenshot's border, app icons on a video's black band), through a flat near-black run
  // that starts at the edge or within a few lines of it (icons reach a little past the box given for them), up to MOST_LETTERBOX.
  const inward = (outside: Line | null, at: (n: number) => Line, span: number): number => {
    let n = 0
    if (outside && outside.spread < FLAT) {
      // A black outside line is a letterbox too (black between the icons on a video's band), with the letterbox's reach.
      const most = dark(outside.colour) ? MOST_LETTERBOX : MOST
      while (n < span * most && like(at(n), outside.colour)) n++
      return n
    }
    const black = (row: Line) => row.spread < FLAT && dark(row.colour)
    while (n < Math.ceil(span * PROBE) && !black(at(n))) n++
    if (!black(at(n))) return 0
    const from = n, colour = at(n).colour
    while (n < span * MOST_LETTERBOX && like(at(n), colour)) n++
    // A thin dark line inside a picture is not a letterbox.
    return n - from >= span * LEAST_LETTERBOX ? n : 0
  }
  const rows = bottom - top, cols = right - left
  bottom -= inward(bottom < height ? line(bottom, left, right, true) : null, n => line(bottom - 1 - n, left, right, true), rows)
  top += inward(top > 0 ? line(top - 1, left, right, true) : null, n => line(top + n, left, right, true), rows)
  right -= inward(right < width ? line(right, top, bottom, false) : null, n => line(right - 1 - n, top, bottom, false), cols)
  left += inward(left > 0 ? line(left - 1, top, bottom, false) : null, n => line(left + n, top, bottom, false), cols)
  return [Math.round(top / height * 1000), Math.round(left / width * 1000), Math.round(bottom / height * 1000), Math.round(right / width * 1000)]
}

/** The box with background bands trimmed; the model's box until the picture has been measured, or when it cannot be. */
export function useTrimmedBox(source: string | null, box: Box | undefined): Box | undefined {
  const key = source && box ? `${source}|${box.join(',')}` : null
  const [result, setResult] = useState<{ key: string; box: Box } | null>(null)
  useEffect(() => {
    if (!key || !source || !box) return
    let live = true
    let pending = trimmed.get(key)
    if (!pending) {
      pending = measure(source, box).catch(() => box)
      trimmed.set(key, pending)
    }
    void pending.then(value => { if (live) setResult({ key, box: value }) })
    return () => { live = false }
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps
  return result?.key === key ? result.box : box
}
